import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { FBAPrepInstruction, PrepInstructionV2 } from "@boxem/core-api-schema";

import styles from "./PackingGroupItem.module.css";

import { FbaBatchItem, FbaBox } from "@/contexts/ShipmentContext";
import Button from "@/components/forms/Button";
import IconCaution from "@/components/icons/IconCaution";
import IconChatInfoFilled from "@/components/icons/IconChatInfoFilled";
import Tooltip from "@/components/Tooltip";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import Flex from "@/components/Flex/Flex";
import ProductImage from "@/components/ProductImage/ProductImage";
import InputNumber from "@/components/forms/InputNumber/InputNumber";

import { prepInstructionIsNone, prepInstructionV2ToString } from "../../utils";
import ProductView from "../ProductView";
import IconBulletList from "@/components/icons/IconBulletList";
import {DraggingState} from "@/app/fba/FbaShipmentFlow/FbaShipmentFlow/DragNDrop";

type PackingGroupItemProps = {
  item: FbaBatchItem;
  quantity: number;
  quantityAssigned: number;
  selectedBoxes: FbaBox[];
  prepInstructions: FBAPrepInstruction[];
  onPrintFNSKUClick: () => void;
  onAssignClick: (item: FbaBatchItem, quantity: number) => void;
  onAssignDragNDrop: (item : FbaBatchItem, quantity: number, targetBox: number) => void;
  openAssignDialog: (item: FbaBatchItem) => void;
  draggingState: DraggingState;
};

export default function PackingGroupItem(props: PackingGroupItemProps) {
  const {
    item,
    quantity,
    quantityAssigned,
    selectedBoxes,
    prepInstructions,
    onPrintFNSKUClick,
    onAssignClick: _onAssignClick,
    onAssignDragNDrop,
    openAssignDialog,
    draggingState,
  } = props;

  const [quantityToAssign, setQuantityToAssign] = useState(quantity - quantityAssigned);
  const [assigningInProgress, setAssigningInProgress] = useState(false);

  const onAssignClick = async (item: FbaBatchItem, quantity: number) => {
    setAssigningInProgress(true);
    await _onAssignClick(item, quantity);
    setAssigningInProgress(false);
  };

  const quantityToAssignRef = useRef(null);
  quantityToAssignRef.current = quantityToAssign;

  useEffect(() => setQuantityToAssign(Math.min(quantityToAssign, quantity - quantityAssigned)), [quantityAssigned]);

  function getQuantityToDrag() {
    let quantityToDrag = quantityToAssignRef.current;
    if(quantityToDrag == 0) quantityToDrag = quantity - quantityAssigned;
    return quantityToDrag;
  }

  function startDrag(e) {
    let ele = (
      <div style={{ transform: "translate(-50%, -100%)", pointerEvents: "none", position: "fixed", backgroundColor: "white", left: "-100px", top: "-100px", zIndex: 10000, display: "flex", flexDirection: "column", alignItems: "center"}} data-drag-item-id={item.id} data-drag-quantity={quantity}>
        <ProductImage imageUrl={item.image} alt={item.name} size={"86px"} />
        <div style={{ position: "absolute", "right": 0, bottom: "-0.6em", background: "var(--static-background-strong)", border: "1px solid", padding: "0 7px", fontWeight: "var(--font-weight-strong)", borderRadius: "10000px" }}>
          <Flex>
            <span className="small-text strong">{getQuantityToDrag()}</span>
          </Flex>
        </div>
      </div>
    );

    const container = document.createElement("div");
    document.body.appendChild(container);
    const reactContainer = createRoot(container);
    reactContainer.render(ele);

    draggingState.isDragging = true;
    draggingState.onDropInBox = (boxId: number) => {
      onAssignDragNDrop(item, getQuantityToDrag(), boxId);
    };

    const itemElement = document.querySelector(`[data-item-id-for-drag-and-drop-gray="${item.id}"]`);
    if(itemElement) {
      const e = itemElement as HTMLElement;
      e.style.transition = "opacity 100ms ease-out";
      e.style.opacity = "0.5";
    }

    draggingState.onCancelOrDone = () => {
      let itemElement = document.querySelector(`[data-item-id-for-drag-and-drop-gray="${item.id}"]`);
      if(itemElement) {
        const e = itemElement as HTMLElement;
        e.style.opacity = "";
      }
    };

    document.body.classList.add("amDraggingNDroppingGiveCursor");

    e.preventDefault();
    return false;
  }

  const assignedAll: boolean = quantityAssigned >= quantity;
  return (
    <div className={`${styles.containerAndDragHandle} ${assignedAll ? styles.success : ""}`} style={{display: "flex", flexDirection: "row", alignItems: "stretch", justifyContent: "spakce-between"}} >
      <div data-item-id-for-drag-and-drop-gray={item.id?.toString()} style={{ flexGrow: 1 }} className={styles.packingGroupItemContainer} >
        <ProductView
          overrideFlexGapForDetails={"8px"}
          item={item}
          quantity={quantity}
          content1={
            <Flex className={`${styles.productStatusItem} ${styles[quantityAssigned < quantity ? "warn" : "success"]}`}>
              {quantityAssigned < quantity && <IconCaution />}
              {quantityAssigned >= quantity && <IconCheckCircle />}
              <p style={{ marginLeft: "1ch" }}>
                {quantityAssigned} / {quantity}
              </p>
            </Flex>
          }
          content2={
            <Button
              type="secondary"
              size="small"
              className={styles.buttonStyles}
              style={{flex: 0}}
              onClick={onPrintFNSKUClick}
            >
              <Flex
                justify="center"
                align="center"
                className={styles.actionButtonContent}
              >
                <IconPrintGoogle />
                Print FNSKU
              </Flex>
            </Button>
          }
          content3={!!prepInstructions && (
            <div className={styles.prepInstructionsContainer}>
              <Tooltip title="Prep instructions are a list of special actions that are required to be taken while preparing this item for shipping">
                <span className={styles.prepInstructions}>
                  <IconChatInfoFilled />
                  <span>&nbsp;Prep Instructions: </span>
                </span>
              </Tooltip>
              {prepInstructions
                .filter(pi => !prepInstructionIsNone(pi.prepType as PrepInstructionV2))
                .map(p => prepInstructionV2ToString(p.prepType as PrepInstructionV2))
                .map(({ name, description }, index) => (
                  <span key={index}>
                    {index > 0 && <span className={styles.comma}>, </span>}
                    <Tooltip key={index} title={`${name}: ${description}`}><span style={{cursor: "help"}}>{name}</span></Tooltip>
                  </span>
                ))
              }
            </div>
          )}
        />
        {quantityAssigned < quantity &&
          <div className={styles.bottomRow}>
            <InputNumber
              min={0}
              max={quantity - quantityAssigned}
              className={styles.quantityInput}
              value={quantityToAssign}
              wrapNumber={true}
              onChange={setQuantityToAssign} />
            <Button
              type="secondary"
              onClick={() => openAssignDialog(item)}
              style={{ padding: "6px" }}
            >
              <IconBulletList />
            </Button>
            <Tooltip
              title={selectedBoxes.length > 1
                ? `${quantityToAssign} ${quantityToAssign === 1 ? "item" : "items"} will be distributed as evenly as possible across all selected boxes`
                : "You need to select boxes to use this button, or use the drag-and-drop handle."
              }
            >
              <Button
                onClick={() => onAssignClick(item, quantityToAssignRef.current)}
                disabled={assigningInProgress || quantityToAssign < 1 || selectedBoxes.length < 1}
                disableWhileOnClick={true}
              >
                  {selectedBoxes.length < 1
                    ? `Assign (${quantityToAssign})` : selectedBoxes.length < 2
                    ? `Assign (${quantityToAssign}) to Box ${selectedBoxes[0].displayIndex}`
                    : `Split (${quantityToAssign}) across ${selectedBoxes.length} boxes`
                  }
              </Button>
            </Tooltip>
          </div>
        }
      </div>
      <div style={{ display: "flex", alignItems: "center", opacity: assignedAll ? 0 : 1, pointerEvents: assignedAll ? "none" : undefined, flexShrink: 1, padding: "24px", paddingLeft: "4px", marginLeft: "-4px", paddingRight: "16px", alignSelf: "center", cursor: "pointer" }} onPointerDownCapture={startDrag} onTouchStartCapture={startDrag}>
        {/* Drag handle*/}
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"> <path d="M6.75 15C6.3375 15 5.98438 14.8531 5.69063 14.5594C5.39687 14.2656 5.25 13.9125 5.25 13.5C5.25 13.0875 5.39687 12.7344 5.69063 12.4406C5.98438 12.1469 6.3375 12 6.75 12C7.1625 12 7.51562 12.1469 7.80938 12.4406C8.10313 12.7344 8.25 13.0875 8.25 13.5C8.25 13.9125 8.10313 14.2656 7.80938 14.5594C7.51562 14.8531 7.1625 15 6.75 15ZM11.25 15C10.8375 15 10.4844 14.8531 10.1906 14.5594C9.89688 14.2656 9.75 13.9125 9.75 13.5C9.75 13.0875 9.89688 12.7344 10.1906 12.4406C10.4844 12.1469 10.8375 12 11.25 12C11.6625 12 12.0156 12.1469 12.3094 12.4406C12.6031 12.7344 12.75 13.0875 12.75 13.5C12.75 13.9125 12.6031 14.2656 12.3094 14.5594C12.0156 14.8531 11.6625 15 11.25 15ZM6.75 10.5C6.3375 10.5 5.98438 10.3531 5.69063 10.0594C5.39687 9.76563 5.25 9.4125 5.25 9C5.25 8.5875 5.39687 8.23438 5.69063 7.94063C5.98438 7.64688 6.3375 7.5 6.75 7.5C7.1625 7.5 7.51562 7.64688 7.80938 7.94063C8.10313 8.23438 8.25 8.5875 8.25 9C8.25 9.4125 8.10313 9.76563 7.80938 10.0594C7.51562 10.3531 7.1625 10.5 6.75 10.5ZM11.25 10.5C10.8375 10.5 10.4844 10.3531 10.1906 10.0594C9.89688 9.76563 9.75 9.4125 9.75 9C9.75 8.5875 9.89688 8.23438 10.1906 7.94063C10.4844 7.64688 10.8375 7.5 11.25 7.5C11.6625 7.5 12.0156 7.64688 12.3094 7.94063C12.6031 8.23438 12.75 8.5875 12.75 9C12.75 9.4125 12.6031 9.76563 12.3094 10.0594C12.0156 10.3531 11.6625 10.5 11.25 10.5ZM6.75 6C6.3375 6 5.98438 5.85313 5.69063 5.55938C5.39687 5.26562 5.25 4.9125 5.25 4.5C5.25 4.0875 5.39687 3.73438 5.69063 3.44063C5.98438 3.14687 6.3375 3 6.75 3C7.1625 3 7.51562 3.14687 7.80938 3.44063C8.10313 3.73438 8.25 4.0875 8.25 4.5C8.25 4.9125 8.10313 5.26562 7.80938 5.55938C7.51562 5.85313 7.1625 6 6.75 6ZM11.25 6C10.8375 6 10.4844 5.85313 10.1906 5.55938C9.89688 5.26562 9.75 4.9125 9.75 4.5C9.75 4.0875 9.89688 3.73438 10.1906 3.44063C10.4844 3.14687 10.8375 3 11.25 3C11.6625 3 12.0156 3.14687 12.3094 3.44063C12.6031 3.73438 12.75 4.0875 12.75 4.5C12.75 4.9125 12.6031 5.26562 12.3094 5.55938C12.0156 5.85313 11.6625 6 11.25 6Z" fill="#767676"/> </svg>
      </div>
    </div>
  );
}
